<template>
  <v-dialog
    v-model="showDialog"
    max-width="550"
    @update:model-value="handleDialogChange">
    <v-card class="dialog-card">
      <div class="pa-3">
        <ModalHeader
          :icon="hasWarnings ? 'mdi-alert-circle' : 'mdi-progress-download'"
          :modalTitle="
            hasWarnings ? 'Export Started (with warnings)' : 'Export Started'
          "
          @close="close" />
        <div
          class="text-caption text-grey-darken-1 mt-1 px-2"
          style="line-height: 1.4">
          <v-icon
            size="14"
            class="mr-1">
            mdi-information-outline
          </v-icon>
          The download is running in the background. Feel free to close this
          dialog; larger archives may take a while to finish.
        </div>
      </div>

      <!-- Data References Section -->
      <div
        class="mx-3 pa-4"
        style="background: rgba(0, 0, 0, 0.06); border-radius: 8px">
        <div class="text-subtitle-1 mb-3 font-weight-bold">
          <v-icon
            class="mr-2"
            size="20">
            mdi-database-export
          </v-icon>
          Data Exported
        </div>

        <div class="stats-grid">
          <div class="stat-item">
            <v-icon
              color="primary"
              size="24">
              mdi-playlist-play
            </v-icon>
            <div class="stat-value">{{ stats.queues }}</div>
            <div class="stat-label">Queues</div>
          </div>

          <div class="stat-item">
            <v-icon
              color="primary"
              size="24">
              mdi-movie-open
            </v-icon>
            <div class="stat-value">{{ stats.scenes }}</div>
            <div class="stat-label">Scenes</div>
          </div>

          <div class="stat-item">
            <v-icon
              color="primary"
              size="24">
              mdi-puzzle-outline
            </v-icon>
            <div class="stat-value">{{ stats.template_refs }}</div>
            <div class="stat-label">Template Refs</div>
          </div>

          <div class="stat-item">
            <v-icon
              color="primary"
              size="24">
              mdi-tune-variant
            </v-icon>
            <div class="stat-value">{{ stats.preset_refs }}</div>
            <div class="stat-label">Preset Refs</div>
          </div>

          <div class="stat-item">
            <v-icon
              color="primary"
              size="24">
              mdi-image-multiple-outline
            </v-icon>
            <div class="stat-value">{{ stats.asset_refs }}</div>
            <div class="stat-label">Asset Refs</div>
          </div>

          <div class="stat-item stat-total">
            <v-icon
              color="success"
              size="24">
              mdi-sigma
            </v-icon>
            <div class="stat-value">{{ stats.total_data }}</div>
            <div class="stat-label">Total Data</div>
          </div>
        </div>
      </div>

      <!-- Files Section -->
      <div
        class="mx-3 mt-3 pa-4"
        style="background: rgba(0, 0, 0, 0.04); border-radius: 8px">
        <div class="text-subtitle-1 mb-3 font-weight-bold">
          <v-icon
            class="mr-2"
            size="20">
            mdi-folder-zip
          </v-icon>
          Files Included
        </div>

        <div class="files-grid">
          <div class="file-item">
            <v-icon
              color="grey"
              size="22">
              mdi-puzzle
            </v-icon>
            <div class="file-value">{{ files.template_files }}</div>
            <div class="file-label">Template Files</div>
          </div>

          <div class="file-item">
            <v-icon
              color="grey"
              size="22">
              mdi-image-multiple
            </v-icon>
            <div class="file-value">{{ files.asset_files }}</div>
            <div class="file-label">Asset Files</div>
          </div>

          <div class="file-item">
            <v-icon
              color="grey"
              size="22">
              mdi-image-frame
            </v-icon>
            <div class="file-value">{{ files.thumbnail_files }}</div>
            <div class="file-label">Thumbnails</div>
          </div>
        </div>

        <div
          v-if="files.total_files === 0"
          class="text-caption text-grey-darken-1 mt-2"
          style="font-style: italic">
          <v-icon
            size="14"
            class="mr-1">
            mdi-information-outline
          </v-icon>
          No files were embedded, only references. Use the "Include files"
          export option to bundle assets and templates into the archive.
        </div>
      </div>

      <!-- Warnings Section (if any) -->
      <div
        v-if="hasWarnings"
        class="mx-3 mt-3 pa-4"
        style="
          background: rgba(255, 152, 0, 0.1);
          border-radius: 8px;
          border: 1px solid rgba(255, 152, 0, 0.3);
        ">
        <div class="text-subtitle-1 mb-2 font-weight-bold">
          <v-icon
            color="warning"
            class="mr-2"
            size="20">
            mdi-alert
          </v-icon>
          Warnings ({{ warnings.length }})
        </div>
        <div class="warnings-list">
          <div
            v-for="(warning, index) in displayedWarnings"
            :key="index"
            class="warning-item">
            <span class="warning-category">{{ warning.category }}</span>
            <span class="warning-item-name">{{ warning.item }}</span>
          </div>
          <div
            v-if="warnings.length > 5"
            class="text-caption text-grey mt-1">
            ... and {{ warnings.length - 5 }} more
          </div>
        </div>
      </div>

      <!-- File Info -->
      <div
        class="mx-3 mt-3 pa-3"
        style="background: rgba(0, 0, 0, 0.03); border-radius: 8px">
        <div class="d-flex align-center">
          <v-icon
            class="mr-2"
            size="18"
            color="grey">
            mdi-file-download
          </v-icon>
          <span class="text-body-2 text-grey">{{ fileName }}</span>
        </div>
        <div
          v-if="estimatedSize > 0"
          class="d-flex align-center mt-2">
          <v-icon
            class="mr-2"
            size="18"
            color="grey">
            mdi-database-arrow-down
          </v-icon>
          <span class="text-body-2 text-grey">
            ~{{ formatBytes(estimatedSize) }} (uncompressed)
          </span>
        </div>
      </div>

      <div class="modal-footer mt-3">
        <v-spacer />
        <v-btn
          @click="close"
          variant="flat"
          color="primary"
          prepend-icon="mdi-close">
          Close
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import ModalHeader from './ModalHeader.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  report: {
    type: Object,
    default: () => ({
      statistics: {
        queues: 0,
        scenes: 0,
        template_refs: 0,
        preset_refs: 0,
        asset_refs: 0,
        total_data: 0,
      },
      files: {
        template_files: 0,
        asset_files: 0,
        thumbnail_files: 0,
        total_files: 0,
      },
      warnings: [],
      warning_count: 0,
      success: true,
    }),
  },
  fileName: {
    type: String,
    default: 'export.cprc',
  },
  estimatedSize: {
    type: Number,
    default: 0,
  },
});

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

const emit = defineEmits(['update:modelValue', 'close']);

const showDialog = ref(props.modelValue);

const stats = computed(() => props.report?.statistics || {});
const files = computed(
  () =>
    props.report?.files || {
      template_files: 0,
      asset_files: 0,
      thumbnail_files: 0,
      total_files: 0,
    }
);
const warnings = computed(() => props.report?.warnings || []);
const hasWarnings = computed(() => warnings.value.length > 0);
const displayedWarnings = computed(() => warnings.value.slice(0, 5));

function handleDialogChange(value) {
  if (!value) {
    close();
  }
}

function close() {
  showDialog.value = false;
  emit('update:modelValue', false);
  emit('close');
}

// Watch for external changes to modelValue
import { watch } from 'vue';
watch(
  () => props.modelValue,
  (newVal) => {
    showDialog.value = newVal;
  }
);
</script>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.stat-total {
  background: rgba(76, 175, 80, 0.1);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 4px;
}

.stat-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
}

.files-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.file-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 8px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

.file-value {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.5);
}

.file-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
}

.warnings-list {
  max-height: 150px;
  overflow-y: auto;
}

.warning-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 0.875rem;
}

.warning-category {
  background: rgba(255, 152, 0, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.warning-item-name {
  color: rgba(255, 255, 255, 0.8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-footer {
  display: flex;
  padding: 16px;
}
</style>
